import { useCallback, useState } from 'react';

interface UseBrowserDownloadResult {
  isDownloading: boolean;
  error?: Error;
  download: (url: string, suggestedFileName?: string) => Promise<void>;
  reset: () => void;
}

function getFileNameFromHeaders(contentDisposition?: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
  const raw = fileNameMatch?.[1] || fileNameMatch?.[2];
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function getFileNameFromUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url, window.location.href);
    const last = parsed.pathname.split('/').filter(Boolean).pop();
    return last || undefined;
  } catch {
    return undefined;
  }
}

export default function useBrowserDownload(): UseBrowserDownloadResult {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const reset = useCallback(() => {
    setIsDownloading(false);
    setError(undefined);
  }, []);

  const download = useCallback(async (url: string, suggestedFileName?: string) => {
    setIsDownloading(true);
    setError(undefined);

    // 1) Try to fetch as blob (best cross-origin compatibility if CORS allows)
    try {
      const target = new URL(url, window.location.href);
      const isCrossOrigin = target.origin !== window.location.origin;
      const response = await fetch(url, {
        mode: 'cors',
        credentials: isCrossOrigin ? 'omit' : 'include'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      const fromHeader = getFileNameFromHeaders(contentDisposition);
      const fromUrl = getFileNameFromUrl(url);
      const fileName = suggestedFileName || fromHeader || fromUrl || 'download';

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      setIsDownloading(false);
      return;
    } catch (err) {
      try {
        const fromUrl = getFileNameFromUrl(url);
        const fileName = suggestedFileName || fromUrl || 'download';
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setIsDownloading(false);
        return;
      } catch (fallbackErr) {
        const e = (fallbackErr instanceof Error ? fallbackErr : new Error('Download failed'));
        setError(e);
        setIsDownloading(false);
        throw e;
      }
    }
  }, []);

  return {
    isDownloading,
    error,
    download,
    reset
  };
}